import { isFreshHeartbeat } from './bridgeStatus';

export const DEFAULT_CROSSFADE_SECONDS = 6;
const MAX_CROSSFADE_SECONDS = 12;

export function crossfadeSecondsFor(settings?: { crossfadeSeconds?: number }): number {
  const seconds = settings?.crossfadeSeconds ?? DEFAULT_CROSSFADE_SECONDS;
  if (!Number.isFinite(seconds)) return DEFAULT_CROSSFADE_SECONDS;
  return Math.min(MAX_CROSSFADE_SECONDS, Math.max(0, seconds));
}

export function fadeVolumeAt(elapsedMs: number, durationMs: number, from: number, to: number): number {
  const clampVolume = (value: number) => Math.min(1, Math.max(0, value));
  if (durationMs <= 0) return clampVolume(to);
  const progress = Math.min(1, Math.max(0, elapsedMs / durationMs));
  return clampVolume(from + (to - from) * progress);
}

export interface LocalFilePlayer {
  deviceId: string;
  eventId: string;
  claimedAt: string;
  heartbeatAt: string;
}

export function parseLocalFilePlayer(raw: unknown): LocalFilePlayer | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.deviceId !== 'string' || typeof value.eventId !== 'string') return null;
  if (typeof value.heartbeatAt !== 'string') return null;
  return {
    deviceId: value.deviceId,
    eventId: value.eventId,
    claimedAt: typeof value.claimedAt === 'string' ? value.claimedAt : '',
    heartbeatAt: value.heartbeatAt,
  };
}

export type LocalPlayerClaim = 'this-device' | 'other-device' | 'none';

export function localPlayerClaimStatus(
  player: LocalFilePlayer | null,
  deviceId: string,
  eventId: string,
  nowMs: number,
): LocalPlayerClaim {
  if (!player || player.eventId !== eventId) return 'none';
  if (player.deviceId === deviceId) return 'this-device';
  return isFreshHeartbeat(player.heartbeatAt, nowMs) ? 'other-device' : 'none';
}

export type QueueLevel = 'bridge' | 'checklist';

export type FileHandoff =
  | { kind: 'wait' }
  | { kind: 'crossfade-to-next-file'; nextId: string }
  | { kind: 'tell-bridge-play' }
  | { kind: 'show-press-play-countdown' };

export function decideFileHandoff(input: {
  timeLeftMs: number;
  crossfadeMs: number;
  level: QueueLevel;
  next: { id: string; isLocalFile: boolean } | null;
  alreadyHandedOff: boolean;
}): FileHandoff {
  const { timeLeftMs, crossfadeMs, level, next, alreadyHandedOff } = input;
  if (alreadyHandedOff) return { kind: 'wait' };
  if (timeLeftMs > Math.max(crossfadeMs, 0)) return { kind: 'wait' };
  if (next?.isLocalFile) return { kind: 'crossfade-to-next-file', nextId: next.id };
  return level === 'bridge' ? { kind: 'tell-bridge-play' } : { kind: 'show-press-play-countdown' };
}

export const PRESS_PLAY_LEAD_MS = 10_000;

export function pressPlayCountdownMs(timeLeftMs: number, crossfadeMs: number): number | null {
  const msUntilPlay = timeLeftMs - crossfadeMs;
  if (msUntilPlay < 0 || msUntilPlay > PRESS_PLAY_LEAD_MS) return null;
  return msUntilPlay;
}

export function compareChecklistOrder<T extends { queuedInPlayer?: boolean; queuedInSpotify?: boolean }>(
  base: (a: T, b: T) => number,
): (a: T, b: T) => number {
  const inPlayer = (item: T) => (item.queuedInPlayer || item.queuedInSpotify ? 0 : 1);
  return (a, b) => inPlayer(a) - inPlayer(b) || base(a, b);
}

export function newFileFadeInMs(input: {
  previousFileStillSounding: boolean;
  cameFromPlayerSong: boolean;
  level: QueueLevel;
  crossfadeMs: number;
}): number {
  if (input.previousFileStillSounding) return input.crossfadeMs;
  if (input.level === 'checklist' && input.cameFromPlayerSong) return input.crossfadeMs;
  return 0;
}

export function shouldAutoClaim(player: LocalFilePlayer | null, eventId: string, nowMs: number): boolean {
  if (!player) return true;
  if (player.eventId === eventId) return false;
  return !isFreshHeartbeat(player.heartbeatAt, nowMs);
}

export function playerSongToPrepare<T extends { localFile?: unknown }>(nowPlaying: T | null, nextUp: T | null): T | null {
  if (!nowPlaying?.localFile || !nextUp || nextUp.localFile) return null;
  return nextUp;
}
